import { Interpolation } from "@emotion/core";
import { transparentize } from "polished";
import { Device, DarkTheme } from "../design-tokens/media-queries";
import { Fonts } from "../design-tokens/fonts";
import { grid } from "../design-tokens/grid";
import { Colors } from "../design-tokens/colors";
import { Border } from "../design-tokens/borders";
import { Keyframes } from "../design-tokens/animation";
import IconLike from "./assets/icon-like.svg";
import IconDislike from "./assets/icon-dislike.svg";
import IconMessage from "./assets/messages-bubble.svg";
import IconLikeLight from "./assets/icon-like-light.svg";
import IconDislikeLight from "./assets/icon-dislike-light.svg";
import IconMessageLight from "./assets/messages-bubble-light.svg";

const heading: Interpolation = {
  fontSize: "22px",
  fontWeight: Fonts.Weight.Bold,
  marginBottom: grid(2),
  animation: `${Keyframes.FadeIn} 0.3s linear`,
};

const standAloneHeading: Interpolation = {
  fontSize: "22px",
  fontWeight: Fonts.Weight.Bold,
  animation: `${Keyframes.FadeIn} 0.3s linear`,
};

const ctas: Interpolation = {
  [Device.DesktopTablet]: {
    display: "flex",
    alignItems: "center",
  },
};

const buttonContainer: Interpolation = {
  display: "flex",
  alignItems: "center",
  animation: `${Keyframes.FadeIn} 0.3s linear`,

  [Device.DesktopTablet]: {
    minWidth: grid(35),
  },
};

const button: Interpolation = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  WebkitAppearance: "none",
  height: grid(5),
  fontSize: "16px",
  fontWeight: Fonts.Weight.SemiBold,
  borderRadius: Border.RadiusSm,
  transition: "all 0.2s ease",
  cursor: "pointer",

  ":hover": {
    outline: 0,
    transform: "translateY(-2px)",
  },

  ":active": {
    outline: 0,
    transform: "scale(1.05)",
  },

  ":focus": {
    outline: 0,
    boxShadow: `0 0 0 3px ${transparentize(0.5, Colors.Blue)}`,
  },
};

const feedbackButton: Interpolation = {
  ...button,
  flex: 1,
  fontSize: "16px",
  fontWeight: Fonts.Weight.SemiBold,
  backgroundColor: Colors.White,
  border: `1px solid ${Colors.BorderGrey}`,

  [DarkTheme]: {
    color: Colors.DarkModeWhite,
    backgroundColor: Colors.DarkModeBackground,
    border: `1px solid ${Colors.DarkModeBorder}`,
  },

  "> img": {
    marginRight: grid(1),
  },

  "&:first-of-type": {
    marginRight: grid(1.5),

    [Device.DesktopTablet]: {
      marginRight: grid(3),
    },
  },
};

const messageButton: Interpolation = {
  [Device.DesktopTablet]: {
    maxWidth: grid(20),
  },
  [Device.Mobile]: {
    width: "100%",
  },
};

const buttonSeparator: Interpolation = {
  opacity: 0.7,

  [Device.DesktopTablet]: {
    marginLeft: grid(2.5),
    marginRight: grid(2.5),
  },

  [Device.Mobile]: {
    textAlign: "center",
    marginTop: grid(1.5),
    marginBottom: grid(1.5),
  },
};

const buttonIcon: Interpolation = {
  width: grid(2.5),
  height: grid(2.5),
  marginRight: grid(1),
};

const iconLike: Interpolation = {
  background: `url(${IconLike}) center no-repeat`,

  [DarkTheme]: {
    background: `url(${IconLikeLight}) center no-repeat`,
  },
};

const iconDislike: Interpolation = {
  background: `url(${IconDislike}) center no-repeat`,

  [DarkTheme]: {
    background: `url(${IconDislikeLight}) center no-repeat`,
  },
};

const iconMessage: Interpolation = {
  background: `url(${IconMessage}) center no-repeat`,

  [DarkTheme]: {
    background: `url(${IconMessageLight}) center no-repeat`,
  },
};

const submitButton: Interpolation = {
  ...button,
  width: grid(13),
  height: grid(4.5),
  color: Colors.White,
  backgroundColor: Colors.Blue,
  border: 0,
  animation: `${Keyframes.FadeIn} 0.3s linear`,
  marginRight: grid(2),

  [DarkTheme]: {
    color: Colors.DarkModeWhite,
  },
};

const cancelButton: Interpolation = {
  ...button,
  width: grid(13),
  height: grid(4.5),
  backgroundColor: Colors.White,
  border: `1px solid ${Colors.BorderGrey}`,
  animation: `${Keyframes.FadeIn} 0.3s linear`,

  [DarkTheme]: {
    color: Colors.DarkModeWhite,
    backgroundColor: Colors.DarkModeBackground,
    border: `1px solid ${Colors.DarkModeBorder}`,
  },
};

const textArea: Interpolation = {
  width: "100%",
  fontSize: "16px",
  padding: grid(1),
  border: `1px solid ${Colors.BorderGrey}`,
  borderRadius: Border.RadiusSm,
  marginBottom: grid(2),
  resize: "none",
  WebkitAppearance: "none",
  animation: `${Keyframes.FadeIn} 0.3s linear`,

  [DarkTheme]: {
    color: Colors.DarkModeWhite,
    backgroundColor: Colors.DarkModeBlack,
    border: `1px solid ${Colors.DarkModeBorder}`,
  },
};

const styles = {
  standAloneHeading,
  heading,
  ctas,
  buttonContainer,
  feedbackButton,
  messageButton,
  buttonSeparator,
  buttonIcon,
  iconLike,
  iconDislike,
  iconMessage,
  textArea,
  submitButton,
  cancelButton,
};

export default styles;
